import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';

interface HexDisplayProps {
    value: string;
    className?: string;
    showCopyIcon?: boolean;
    tooltipSide?: "top" | "bottom" | "left" | "right";
}

export function HexDisplay({
    value,
    className,
    showCopyIcon = false,
    tooltipSide = "top"
}: HexDisplayProps) {
    const [copied, setCopied] = useState(false);

    const handleCopy = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    // It's generally better to have a single TooltipProvider at the root of your application.
    // Adding it here for component encapsulation, but consider moving it higher up your component tree.
    return (
        <TooltipProvider delayDuration={300}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span
                        className={cn(
                            "font-mono cursor-pointer hover:text-primary transition-colors select-none inline-flex items-center gap-1",
                            className
                        )}
                        onClick={handleCopy}
                    >
                        <span className="truncate">{value}</span>
                        {showCopyIcon && (
                            <span className="inline-block flex-shrink-0">
                                {copied ? (
                                    <Check className="h-3 w-3 text-green-500" />
                                ) : (
                                    <Copy className="h-3 w-3 opacity-50 hover:opacity-100" />
                                )}
                            </span>
                        )}
                    </span>
                </TooltipTrigger>
                <TooltipContent
                    side={tooltipSide}
                    className="font-mono z-[9999]" // Ensure high z-index, Radix default is z-50
                    style={{
                        maxWidth: 'calc(100vw - 32px)',
                        wordBreak: 'break-all',
                        whiteSpace: 'pre-wrap', // Allows text to wrap to multiple lines
                    }}
                >
                    <p>{copied ? 'Copied to clipboard!' : value}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

// Convenience components for specific use cases
export function AddressDisplay({ address, className, tooltipSide }: {
    address: string;
    className?: string;
    tooltipSide?: "top" | "bottom" | "left" | "right";
}) {
    return (
        <HexDisplay
            value={address}
            className={className}
            tooltipSide={tooltipSide}
            showCopyIcon={true} // Typically, addresses should have copy icon
        />
    );
}

export function DigestDisplay({ digest, className, tooltipSide }: {
    digest: string;
    className?: string;
    tooltipSide?: "top" | "bottom" | "left" | "right";
}) {
    return (
        <HexDisplay
            value={digest}
            className={className}
            tooltipSide={tooltipSide}
            showCopyIcon={true} // Digests too
        />
    );
} 